import React from 'react';
import type { UserDraft } from '../../components/CreateUpdateUserModal/component';
import useAuthContext from '../../hooks/useAuthContext';
import { Loader, ReloadIconButton, UserRoleChip, ConfirmationDialog } from '../../elements';
import { DEFAULT_ROWS_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE, LoaderSize } from '../../constants/constants';
import {
  notifications,
  extractApiErrorMessage,
  extractApiErrorCode,
  AuthErrorCode,
  UserAdminErrorCode
} from '../../utils';
import { Button, Chip, Divider, Icon, IconButton, Menu, MenuItem, TablePagination, Tooltip } from '@mui/material';
import { V4_ICON_BUTTON_SMALL } from '../../theme/v4Compat';
import { useFarmDateTime } from '../../hooks/useFarmDateTime';
import { ApiService } from '../../hooks';
import { CreateUpdateUserModal, InvitationLinkDialog } from '../../components';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import UserRole from '../../constants/user-role';
import { LocationStable, LocationStableTranslations } from '../../models/locations';
import { AcceptedDaysOfWeekOrdered, DayOfWeekTranslations } from '../../models/common';
import { HealthSystemDomainUsersResultUserViewModel } from '../../api/HealthSystemApi';

// Invite mode for CreateUpdateUserModal: no id, nothing filled in yet.
const EMPTY_USER: UserDraft = {};

interface RowAction {
  key: string;
  icon: string;
  label: string;
  onClick: () => void;
  /** rendered in red and set apart from the rest - the action takes access away */
  destructive?: boolean;
  /** draw a divider above this action */
  separated?: boolean;
}

/**
 * The generated client is only regenerated by `bun run swagger`, so it does not carry the client app
 * columns yet. Widening the row here keeps the page honest until it does, after which this alias is
 * redundant rather than wrong.
 */
type UserRow = HealthSystemDomainUsersResultUserViewModel & {
  appVersion?: string | null;
  appOs?: string | null;
  clientAppUpdatedAt?: string | null;
};

const UsersPage: React.FC<any> = () => {
  const navigate = useNavigate();
  const { state } = useAuthContext();
  const { t } = useTranslation();
  const { formatDate, formatTime } = useFarmDateTime();

  const apiService = new ApiService();

  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);

  const [createUserDialogOpen, setCreateUserDialogOpen] = React.useState(false);
  const [selectedUser, setSelectedUser] = React.useState<UserDraft>(EMPTY_USER);
  const [resendInvitationUser, setResendInvitationUser] =
    React.useState<HealthSystemDomainUsersResultUserViewModel | null>(null);
  const [activationUser, setActivationUser] = React.useState<HealthSystemDomainUsersResultUserViewModel | null>(null);
  const [invitationLinkUser, setInvitationLinkUser] = React.useState<HealthSystemDomainUsersResultUserViewModel | null>(
    null
  );

  // the row whose overflow menu is open, and the button it hangs off
  const [menuUser, setMenuUser] = React.useState<HealthSystemDomainUsersResultUserViewModel | null>(null);
  const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null);

  const canManageUsers = state.role === UserRole.manager || state.role === UserRole.admin;

  const [users, setUsers] = React.useState<HealthSystemDomainUsersResultUserViewModel[]>([]);

  const loadUsers = async () => {
    setIsBusy(true);

    const searchPage = page === 0 ? 1 : page + 1;

    apiService.api.users
      .usersGetUsers({ Page: searchPage, Size: rowsPerPage })
      .then((result) => {
        setUsers(result.data.data);
        setTotal(result.data.total);
      })
      .catch(() => {
        notifications.error(t('f-usr-load-error'));
      })
      .finally(() => {
        setIsBusy(false);
        setLoaded(true);
      });
  };

  React.useEffect(() => {
    if (loaded === false) {
      const loadData = async () => loadUsers();
      loadData();
    }
  }, [loaded]);

  // pagination
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE);
  const [total, setTotal] = React.useState(0);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setLoaded(false);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setLoaded(false);
  };
  // * pagination

  const handleCreateUpdateUserAction = (result: boolean | HealthSystemDomainUsersResultUserViewModel | null) => {
    setCreateUserDialogOpen(false);

    if (result === null) {
      return;
    }

    if (result === true) {
      setLoaded(false);
    }
  };

  const openCreateUserDialog = () => {
    setSelectedUser(EMPTY_USER);
    setCreateUserDialogOpen(true);
  };

  const openUpdateUserDialog = (user: HealthSystemDomainUsersResultUserViewModel) => {
    setSelectedUser({ ...user });
    setCreateUserDialogOpen(true);
  };

  const viewUser = (id: string) => {
    navigate(`/user/${id}`);
  };

  const handleResendInvitation = () => {
    if (resendInvitationUser === null) {
      return;
    }

    const user = resendInvitationUser;
    setResendInvitationUser(null);
    setIsBusy(true);

    apiService.api.userAccounts
      .userAccountsResendInvitation({ id: user.id }, { format: 'json' })
      .then(() => {
        notifications.success(t('f-usr-resend-invitation-success'));
      })
      .catch((e: unknown) => {
        if (extractApiErrorCode(e) === AuthErrorCode.UserAccountNotFound) {
          notifications.error(t('f-usr-resend-invitation-not-found'));
        } else {
          notifications.error(extractApiErrorMessage(e, { fallback: t('f-usr-resend-invitation-error') }));
        }
      })
      .finally(() => setIsBusy(false));
  };

  const handleActivationChange = () => {
    if (activationUser === null) {
      return;
    }

    const user = activationUser;
    const deactivating = !user.isDeactivated;

    setActivationUser(null);
    setIsBusy(true);

    const request = deactivating
      ? apiService.api.users.usersDeactivateUser({ userId: user.id }, { format: 'json' })
      : apiService.api.users.usersActivateUser({ userId: user.id }, { format: 'json' });

    request
      .then(() => {
        notifications.success(t(deactivating ? 'f-usr-deactivate-success' : 'f-usr-activate-success'));
        setLoaded(false);
      })
      .catch((e: unknown) => {
        notifications.error(getActivationErrorMessage(e, deactivating));
      })
      .finally(() => setIsBusy(false));
  };

  const getActivationErrorMessage = (error: unknown, deactivating: boolean) => {
    const fallback = t(deactivating ? 'f-usr-deactivate-error' : 'f-usr-activate-error');

    switch (extractApiErrorCode(error)) {
      case UserAdminErrorCode.CannotDeactivateSelf:
        return t('f-usr-deactivate-self-error');
      case UserAdminErrorCode.NotAllowedToModify:
        return t('f-usr-deactivate-forbidden-error');
      case UserAdminErrorCode.NotFound:
        return t('f-usr-deactivate-not-found');
      default:
        return extractApiErrorMessage(error, { fallback });
    }
  };

  /**
   * The row actions, in menu order. A row carries between one and four of them depending on the
   * viewer's role and the account's state, which is why they live in a menu rather than in a line of
   * icon buttons: the column keeps one width and each action carries a name instead of a glyph alone.
   */
  const getRowActions = (user: HealthSystemDomainUsersResultUserViewModel): RowAction[] => {
    const actions: RowAction[] = [
      {
        key: 'set',
        icon: 'edit',
        label: t('f-usr-sett-tool'),
        onClick: () => openUpdateUserDialog(user)
      }
    ];

    // Both invitation actions only mean anything until the invitation is accepted.
    if (canManageUsers && !user.isActivated && !user.isDeactivated) {
      actions.push({
        key: 'resend-invitation',
        icon: 'send',
        label: t('f-usr-resend-invitation-tool'),
        onClick: () => setResendInvitationUser(user)
      });

      actions.push({
        key: 'invitation-link',
        icon: 'link',
        label: t('f-usr-invitation-link-tool'),
        onClick: () => setInvitationLinkUser(user)
      });
    }

    // Deactivating your own account is refused by the API, so it is not offered here either.
    if (canManageUsers && user.id !== state.userId) {
      actions.push({
        key: 'activation',
        icon: user.isDeactivated ? 'lock_open' : 'block',
        label: t(user.isDeactivated ? 'f-usr-activate-tool' : 'f-usr-deactivate-tool'),
        onClick: () => setActivationUser(user),
        // reactivating restores an account, deactivating revokes one - only the second takes access away
        destructive: !user.isDeactivated,
        separated: true
      });
    }

    return actions;
  };

  const openRowMenu = (event: React.MouseEvent<HTMLElement>, user: HealthSystemDomainUsersResultUserViewModel) => {
    setMenuAnchor(event.currentTarget);
    setMenuUser(user);
  };

  const closeRowMenu = () => {
    setMenuUser(null);
    setMenuAnchor(null);
  };

  const runRowAction = (action: RowAction) => {
    closeRowMenu();
    action.onClick();
  };

  const renderActionCell = (user: HealthSystemDomainUsersResultUserViewModel) => {
    return (
      <div className="flex items-center justify-end gap-1 whitespace-nowrap">
        <Tooltip arrow title={t('f-usr-view-tool')}>
          <IconButton
            aria-label={t('f-usr-view-tool') + ': ' + (user.name || user.email)}
            id={`user-action-${user.id}`}
            size="small"
            color="primary"
            onClick={() => {
              viewUser(user.id);
            }}
            sx={V4_ICON_BUTTON_SMALL}
          >
            <Icon fontSize="small">{'visibility'}</Icon>
          </IconButton>
        </Tooltip>

        <Tooltip arrow title={t('f-com-actions')}>
          <IconButton
            aria-label={t('f-com-actions') + ': ' + (user.name || user.email)}
            id={`user-menu-${user.id}`}
            size="small"
            aria-haspopup="true"
            aria-controls="userRowActionsMenu"
            onClick={(event) => openRowMenu(event, user)}
            sx={V4_ICON_BUTTON_SMALL}
          >
            <Icon fontSize="small">{'more_vert'}</Icon>
          </IconButton>
        </Tooltip>
      </div>
    );
  };

  const renderRowMenuItems = () => {
    if (menuUser === null) {
      return null;
    }

    const user = menuUser;

    return getRowActions(user).flatMap((action) => {
      const item = (
        <MenuItem key={action.key} id={`user-${action.key}-${user.id}`} onClick={() => runRowAction(action)}>
          <span className="flex items-center gap-3 pr-4">
            <Icon fontSize="small" color={action.destructive ? 'error' : 'primary'}>
              {action.icon}
            </Icon>
            <span className={action.destructive ? 'text-red-600' : undefined}>{action.label}</span>
          </span>
        </MenuItem>
      );

      return action.separated ? [<Divider key={`${action.key}-divider`} />, item] : [item];
    });
  };

  /**
   * Three independent states, in order of precedence: deactivated by an administrator, invited but
   * never activated, and active. The first is reversible only by an administrator; the second only
   * by the user accepting their invitation.
   */
  const renderStatusCell = (user: HealthSystemDomainUsersResultUserViewModel) => {
    if (user.isDeactivated) {
      return <Chip size="small" label={t('f-usr-status-deactivated')} className="!bg-red-100 !text-red-800" />;
    }

    return user.isActivated ? (
      <Chip size="small" label={t('f-com-active')} className="!bg-green-100 !text-green-800" />
    ) : (
      <Chip size="small" label={t('f-usr-status-invited')} className="!bg-amber-100 !text-amber-800" />
    );
  };

  /**
   * The backend records this from the headers the mobile app sends. Empty for every web frontend
   * user, and for a farm user who has not opened the app since it started being recorded - which is
   * why an empty cell must read as "not known", not as "old version".
   */
  const renderAppVersionCell = (row: UserRow) => {
    if (!row.appVersion) {
      return '-';
    }

    return (
      <Tooltip
        arrow
        title={
          row.clientAppUpdatedAt ? formatDate(row.clientAppUpdatedAt) + ' ' + formatTime(row.clientAppUpdatedAt) : ''
        }
      >
        <div className="flex flex-col leading-tight">
          <span>{row.appVersion}</span>
          <span className="text-xs text-gray-500">{row.appOs}</span>
        </div>
      </Tooltip>
    );
  };

  const renderLocationStablesCell = (row: HealthSystemDomainUsersResultUserViewModel) => (
    <div className="flex max-w-[280px] flex-wrap gap-1">
      {row.stables?.length ? (
        row.stables.map((stable) => (
          <span key={stable} className="rounded bg-slate-100 px-2 py-1 text-xs text-slate-600">
            {t(LocationStableTranslations[stable as LocationStable])}
          </span>
        ))
      ) : (
        <span className="text-slate-400">—</span>
      )}
    </div>
  );

  const renderAvailabilityCell = (row: HealthSystemDomainUsersResultUserViewModel) => (
    <div className="flex gap-1" aria-label={t('f-employees-availability')}>
      {AcceptedDaysOfWeekOrdered.map((day) => {
        const available = row.availability?.includes(day);
        const label = t(DayOfWeekTranslations[day]);
        return (
          <Tooltip
            key={day}
            title={
              label +
              ': ' +
              (available
                ? t('f-task-workspace-available', 'Available')
                : t('f-task-workspace-unavailable', 'Unavailable'))
            }
          >
            <span
              className={
                'flex h-7 w-7 items-center justify-center rounded text-xs ' +
                (available ? 'bg-indigo-50 font-semibold text-indigo-700' : 'bg-slate-50 text-slate-400')
              }
              aria-label={
                label +
                ': ' +
                (available
                  ? t('f-task-workspace-available', 'Available')
                  : t('f-task-workspace-unavailable', 'Unavailable'))
              }
            >
              {label.slice(0, 2)}
            </span>
          </Tooltip>
        );
      })}
    </div>
  );

  return (
    <>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="m-0 text-lg font-semibold text-slate-900">{t('f-usr-title')}</h2>
            <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">{total}</span>
          </div>
          <p className="mt-1 mb-0 text-sm text-slate-500">
            {t('f-task-workspace-staff-help', 'Manage farm access, stable assignments and weekly availability.')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ReloadIconButton action={() => setLoaded(false)} />
          {canManageUsers && (
            <Button
              id="createUserBtn"
              disabled={isBusy}
              color="primary"
              variant="contained"
              className="!normal-case !shadow-none"
              startIcon={<Icon>person_add</Icon>}
              onClick={openCreateUserDialog}
            >
              {t('f-usr-invite-title')}
            </Button>
          )}
        </div>
      </div>
      <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />
      {loaded && (
        <div className="overflow-hidden rounded-lg border border-slate-200">
          <div className="overflow-x-auto">
            <table id="usersResultTable" className="w-full text-left text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs text-slate-500">
                <tr>
                  <th scope="col" className="px-4 py-3 font-medium">
                    {t('f-usr-name')}
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium">
                    {t('f-usr-role')}
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium">
                    {t('f-employees-stables')}
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium">
                    {t('f-employees-availability')}
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium">
                    {t('f-usr-status')}
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium">
                    {t('f-usr-app-version')}
                  </th>
                  <th scope="col" className="px-4 py-3 text-right font-medium">
                    {t('f-com-actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map((row) => (
                  <tr key={row.id} className="hover:bg-slate-50/70">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <span
                          aria-hidden="true"
                          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-xs font-semibold text-indigo-700"
                        >
                          {(row.name || row.email || '?')
                            .split(/\s+/)
                            .map((part) => part[0])
                            .slice(0, 2)
                            .join('')
                            .toUpperCase()}
                        </span>
                        <div className="min-w-[180px]">
                          <button
                            type="button"
                            className="cursor-pointer border-0 bg-transparent p-0 text-left font-semibold text-slate-800 hover:text-indigo-700 focus-visible:outline-2 focus-visible:outline-indigo-600"
                            onClick={() => viewUser(row.id)}
                          >
                            {row.name || row.email}
                          </button>
                          <div className="mt-1 text-xs text-slate-500">{row.email}</div>
                          <div className="mt-1 text-xs text-slate-400">
                            {t('f-usr-lang')}: {row.languageIso || '—'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <UserRoleChip role={row.role ?? ''} />
                    </td>
                    <td className="min-w-[220px] px-4 py-4">{renderLocationStablesCell(row)}</td>
                    <td className="px-4 py-4">{renderAvailabilityCell(row)}</td>
                    <td className="px-4 py-4">{renderStatusCell(row)}</td>
                    <td className="px-4 py-4 text-xs text-slate-500">{renderAppVersionCell(row)}</td>
                    <td className="px-4 py-4">{renderActionCell(row)}</td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-slate-500">
                      {t('f-no-data')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-200">
            <TablePagination
              component="div"
              rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
              count={total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </div>
        </div>
      )}

      <Menu
        id="userRowActionsMenu"
        anchorEl={menuAnchor}
        open={menuUser !== null}
        onClose={closeRowMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {renderRowMenuItems()}
      </Menu>

      <CreateUpdateUserModal
        key={createUserDialogOpen ? 'add-new' : selectedUser.id}
        opened={createUserDialogOpen}
        onModalAction={handleCreateUpdateUserAction}
        returnObject={false}
        user={selectedUser}
      />

      <ConfirmationDialog
        dialogId="userActivationDialog"
        open={activationUser !== null}
        title={t(activationUser?.isDeactivated ? 'f-usr-activate-title' : 'f-usr-deactivate-title')}
        message={t(activationUser?.isDeactivated ? 'f-usr-activate-confirm' : 'f-usr-deactivate-confirm', {
          user: activationUser?.name || activationUser?.email || ''
        })}
        alertSeverity={activationUser?.isDeactivated ? 'info' : 'warning'}
        onSubmit={handleActivationChange}
        onClose={() => setActivationUser(null)}
      />

      <ConfirmationDialog
        dialogId="resendInvitationDialog"
        open={resendInvitationUser !== null}
        title={t('f-usr-resend-invitation-title')}
        message={t('f-usr-resend-invitation-confirm', {
          user: resendInvitationUser?.name || resendInvitationUser?.email || ''
        })}
        alertSeverity="info"
        onSubmit={handleResendInvitation}
        onClose={() => setResendInvitationUser(null)}
      />

      <InvitationLinkDialog
        open={invitationLinkUser !== null}
        user={invitationLinkUser}
        onClose={() => setInvitationLinkUser(null)}
      />
    </>
  );
};

export default UsersPage;
